import React, {useState, useEffect, useRef} from 'react';
import {useHistory} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputMask} from "primereact/inputmask";
import {Panel} from "primereact/panel";
import axios from "axios";
import {TriStateCheckbox} from 'primereact/tristatecheckbox';

const EmployeeListComponent = (props) => {

  const {t, onDetailedSearchActiveChange} = props;
  const history = useHistory();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const LIST_STORAGE_KEY = 'lastEmployeeListState';
  const DS_STORAGE_KEY = 'lastEmployeeDetailedSearchForm';
  const DEFAULT_SORT_FIELD = 'last_name';
  const DEFAULT_SORT_ORDER = -1;
  const savedListState = localStorage.getItem(LIST_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(LIST_STORAGE_KEY))
    : null;
  const [rows, setRows] = useState(
    savedListState?.rows || Number(process.env.REACT_APP_LIST_ROW_DEFAULT)
  );
  const DEFAULT_DETAILED_SEARCH_QUERY = 'search[active][operator]==&search[active][value]=true';
  const [detailedSearchQuery, setDetailedSearchQuery] = useState(
    savedListState?.detailedSearchQuery ?? DEFAULT_DETAILED_SEARCH_QUERY
  );
  const isDetailedSearchActive = React.useMemo(() => {
    return (detailedSearchQuery || '').toString().trim().length > 0;
  }, [detailedSearchQuery]);
  const dt = useRef(null);
  const savedDetailedForm = localStorage.getItem(DS_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(DS_STORAGE_KEY))
    : null;

  const [dsLastName, setDsLastName] = useState(savedDetailedForm?.dsLastName ?? '');
  const [dsFirstName, setDsFirstName] = useState(savedDetailedForm?.dsFirstName ?? '');
  const [dsEmployeePosition, setDsEmployeePosition] = useState(savedDetailedForm?.dsEmployeePosition ?? null);
  const [dsEmail, setDsEmail] = useState(savedDetailedForm?.dsEmail ?? '');
  const [dsInsuranceNumber, setDsInsuranceNumber] = useState(savedDetailedForm?.dsInsuranceNumber ?? '');
  const [dsTaxNumber, setDsTaxNumber] = useState(savedDetailedForm?.dsTaxNumber ?? '');
  const [dsIdentityDocumentType, setDsIdentityDocumentType] = useState(savedDetailedForm?.dsIdentityDocumentType ?? null);
  const [dsIdentityDocumentNumber, setDsIdentityDocumentNumber] = useState(savedDetailedForm?.dsIdentityDocumentNumber ?? '');
  const [dsIdentityDocumentValidUntil, setDsIdentityDocumentValidUntil] = useState(savedDetailedForm?.dsIdentityDocumentValidUntil ?? '');
  const [dsPhoneNumber, setDsPhoneNumber] = useState(savedDetailedForm?.dsPhoneNumber ?? '');
  const [dsBirthDay, setDsBirthDay] = useState(savedDetailedForm?.dsBirthDay ?? '');
  const [dsBirthPlace, setDsBirthPlace] = useState(savedDetailedForm?.dsBirthPlace ?? '');
  const [dsCitizenship, setDsCitizenship] = useState(savedDetailedForm?.dsCitizenship ?? null);
  const [dsNote, setDsNote] = useState(savedDetailedForm?.dsNote ?? '');
  const [dsActive, setDsActive] = useState(
    savedDetailedForm && Object.prototype.hasOwnProperty.call(savedDetailedForm, 'dsActive')
      ? savedDetailedForm.dsActive
      : true
  );
  const [employeePositionOptions, setEmployeePositionOptions] = useState([]);
  const [identityDocumentTypeOptions, setIdentityDocumentTypeOptions] = useState([]);
  const [citizenshipOptions, setCitizenshipOptions] = useState([]);

  const [lazyParams, setLazyParams] = useState(() => {
    const savedLazyParams = savedListState?.lazyParams;
    if (savedLazyParams) {
      return {
        ...savedLazyParams,
        first: savedLazyParams.first ?? 0,
        rows: savedLazyParams.rows ?? rows,
        page: savedLazyParams.page ?? 0,
        sortField: savedLazyParams.sortField || DEFAULT_SORT_FIELD,
        sortOrder: typeof savedLazyParams.sortOrder === 'number' ? savedLazyParams.sortOrder : DEFAULT_SORT_ORDER,
      };
    }
    return {
      first: 0,
      rows: rows,
      page: 0,
      sortField: DEFAULT_SORT_FIELD,
      sortOrder: DEFAULT_SORT_ORDER,
    };
  });

  const persistDetailedForm = () => {
    const payload = {
      dsLastName,
      dsFirstName,
      dsEmployeePosition,
      dsEmail,
      dsInsuranceNumber,
      dsTaxNumber,
      dsIdentityDocumentType,
      dsIdentityDocumentNumber,
      dsIdentityDocumentValidUntil,
      dsPhoneNumber,
      dsBirthDay,
      dsBirthPlace,
      dsCitizenship,
      dsNote,
      dsActive,
    };
    localStorage.setItem(DS_STORAGE_KEY, JSON.stringify(payload));
  };

  const clearDetailedFormPersist = () => {
    localStorage.removeItem(DS_STORAGE_KEY);
  };

  let loadLazyTimeout = null;
  const loadLazyData = () => {
    setLoading(true);
    if (loadLazyTimeout) {
      clearTimeout(loadLazyTimeout);
    }
    loadLazyTimeout = setTimeout(() => {
      let query = window.App.transformQueryParams(lazyParams)
      if (detailedSearchQuery) {
        query += (query.includes('?') ? '&' : '?') + detailedSearchQuery;
      }
      apiCall('get', '/api/v2/employee' + query)
        .then(response => {
          setTotalRecords(response.data.data.total_item_count);
          setEmployees(response.data.data.items)
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
      setLoading(false);
        })
    }, Math.random() * 1000 + 250);
  }
  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onFilter = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    _lazyParams['first'] = 0;
    _lazyParams['page'] = 0;
    setLazyParams(_lazyParams);
  }
  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: Number(process.env.REACT_APP_LIST_ROW_OP1), value: Number(process.env.REACT_APP_LIST_ROW_OP1)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP2), value: Number(process.env.REACT_APP_LIST_ROW_OP2)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP3), value: Number(process.env.REACT_APP_LIST_ROW_OP3)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP4), value: Number(process.env.REACT_APP_LIST_ROW_OP4)},
      ];

      return (
        <>
          {/*<span className="p-mx-1" style={{color: 'var(--text-color)', userSelect: 'none'}}>Tételek egy oldalon: </span>*/}
          <Dropdown value={options.value} options={dropdownOptions} onChange={(e) => setRows(e.target.value)}
                    appendTo={document.body}/>
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', userSelect: 'none', width: '120px', textAlign: 'center'}}>
                        {options.first} - {options.last} / {options.totalRecords}
        </span>
      )
    }
  };

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
  }

  // const handleRowDoubleClick = (event) => {
  //   if (props.view === 'search') {
  //     const row = event.data;
  //     history.push(
  //       props.backlink +
  //       (props.backlink.includes('?') ? '&' : '?') +
  //       'employeeId=' + row.id +
  //       '&employeeName=' + encodeURIComponent(row.first_name + ' ' + row.last_name)
  //     );
  //   }
  // };

  const buildDetailedSearchQuery = () => {
    let parts = [];
    if (dsLastName && dsLastName.length > 0) {
      parts.push(`search[last_name][operator]=like&search[last_name][value]=${dsLastName}`);
    }
    if (dsFirstName && dsFirstName.length > 0) {
      parts.push(`search[first_name][operator]=like&search[first_name][value]=${dsFirstName}`);
    }
    if (dsEmployeePosition) {
      parts.push(`search[employee_position_name][operator]==&search[employee_position_name][value]=${dsEmployeePosition}`);
    }
    if (dsEmail && dsEmail.length > 0) {
      parts.push(`search[email][operator]=like&search[email][value]=*${dsEmail}*`);
    }
    if (dsInsuranceNumber && dsInsuranceNumber.length > 0) {
      parts.push(`search[insurance_number][operator]=like&search[insurance_number][value]=${dsInsuranceNumber}`);
    }
    if (dsTaxNumber && dsTaxNumber.length > 0) {
      parts.push(`search[tax_number][operator]=like&search[tax_number][value]=${dsTaxNumber}`);
    }
    if (dsIdentityDocumentNumber && dsIdentityDocumentNumber.length > 0) {
      parts.push(`search[identity_document_number][operator]=like&search[identity_document_number][value]=${dsIdentityDocumentNumber}`);
    }
    if (dsIdentityDocumentType) {
      parts.push(`search[identity_document_type][operator]==&search[identity_document_type][value]=${dsIdentityDocumentType}`);
    }
    if (dsIdentityDocumentValidUntil && dsIdentityDocumentValidUntil.length > 0) {
      parts.push(`search[identity_document_valid_until][operator]==&search[identity_document_valid_until][value]=${dsIdentityDocumentValidUntil}`);
    }
    if (dsPhoneNumber && dsPhoneNumber.length > 0) {
      parts.push(`search[phone_number][operator]=like&search[phone_number][value]=${dsPhoneNumber}`);
    }
    if (dsBirthPlace && dsBirthPlace.length > 0) {
      parts.push(`search[birth_place][operator]=like&search[birth_place][value]=${dsBirthPlace}`);
    }
    if (dsBirthDay && dsBirthDay.length > 0) {
      parts.push(`search[birth_day][operator]==&search[birth_day][value]=${dsBirthDay}`);
    }
    if (dsCitizenship) {
      parts.push(`search[citizenship][operator]==&search[citizenship][value]=${dsCitizenship}`);
    }
    if (dsNote && dsNote.length > 0) {
      parts.push(`search[note][operator]==&search[note][value]=${dsNote}`);
    }
    if (dsActive !== null) {
      parts.push(`search[active][operator]==&search[active][value]=${dsActive}`);
    }
    return parts.filter(Boolean).join('&');
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <Button icon="pi pi-eye" className=" p-button p-mr-2"
                tooltip={t('view')} tooltipOptions={{position: 'left'}}
                onClick={() => {
                  localStorage.setItem('breadcrumb', JSON.stringify([
                      {label: 'HR', url: '/#/hr/employee'},
                      {label: rowData.name, url: '/#/hr/employee/read/' + rowData.id},
                    ])
                  )
                  history.push('/hr/employee/read/' + rowData.id)
                }}/>
        {localStorage.getItem('roles').toString().includes('role_employee_write') &&
          <Button icon="pi pi-pencil" className="  p-button-warning p-mr-2"
                  tooltip={t('edit')} tooltipOptions={{position: 'left'}}
                  onClick={() => {
                    history.push('/hr/employee/update/' + rowData.id)
                  }}/>
        }
        {localStorage.getItem('roles').toString().includes('role_employee_write') &&
          <Button icon="pi pi-trash" className="p-button-danger"
                  tooltip={t('delete')} tooltipOptions={{position: 'left'}}
                  onClick={() => {
                    if (window.confirm(t('confirmDelete'))) {
                      setLoading(true);
                      apiCall('delete', '/api/v2/employee/' + rowData.id)
                        .then(response => {
                          window.App.toastShow('success', t('toastDeleteSuccess'));
                          window.App.setState({rerenderEmployeeList: Math.random()})
                        })
                        .catch(error => {
                          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
                        })
                        .finally(() => {
                          setLoading(false);
                        })
                    }
                  }}
          />
        }
      </React.Fragment>
    );
  }

  useEffect(() => {
    apiCall('get', '/api/v2/employee-position')
      .then(response => {
        setEmployeePositionOptions(response.data.data.items || []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });

    apiCall('get', '/api/v2/dictionary/identity-document-type')
      .then(response => {
        setIdentityDocumentTypeOptions(response.data.data || []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });

    apiCall('get', '/api/v2/citizenship')
      .then(response => {
        setCitizenshipOptions(response.data.data.items || []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  useEffect(() => {
    loadLazyData();
  }, [lazyParams, window.App.state.rerenderEmployeeList])

  useEffect(() => {
    localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify({
      rows,
      lazyParams,
      detailedSearchQuery,
      sortField: lazyParams.sortField || DEFAULT_SORT_FIELD,
      sortOrder: typeof lazyParams.sortOrder === 'number' ? lazyParams.sortOrder : DEFAULT_SORT_ORDER,
    }));
  }, [rows, lazyParams, detailedSearchQuery]);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      rows: rows
    }));
  }, [rows]);

  useEffect(() => {
    if (typeof onDetailedSearchActiveChange === 'function') {
      onDetailedSearchActiveChange(isDetailedSearchActive);
    }
  }, [isDetailedSearchActive, onDetailedSearchActiveChange]);

  return (
    <>
      {props.view !== 'advanced' && (props.detailedSearchVisible || props.view === 'search') &&
              <Panel className="p-mb-2" onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (document.activeElement) document.activeElement.blur();
                    setTimeout(() => {
                      const searchBtn = document.querySelector('button.p-button-primary .pi-search')?.parentElement;
                      if (searchBtn) searchBtn.click();
                    }, 100);
                  }
                }}>
                <div className={"p-grid"}>
                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldLastName')}</label>
                      <InputText
                        value={dsLastName}
                        onChange={(e) => setDsLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldFirstName')}</label>
                      <InputText
                        value={dsFirstName}
                        onChange={(e) => setDsFirstName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('columnPosition')}</label>
                      <Dropdown
                        value={dsEmployeePosition}
                        options={(employeePositionOptions || []).map(p => ({ label: p.name, value: p.name }))}
                        onChange={(e) => setDsEmployeePosition(e.value)}
                        showClear
                        placeholder={t('dsSelect')}
                      />
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('columnEmail')}</label>
                      <InputText
                        value={dsEmail}
                        onChange={(e) => setDsEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldInsuranceNumber')}</label>
                      <InputText
                        value={dsInsuranceNumber}
                        onChange={(e) => setDsInsuranceNumber(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('columnTaxNumber')}</label>
                      <InputText
                        value={dsTaxNumber}
                        onChange={(e) => setDsTaxNumber(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldIdentityDocumentType')}</label>
                      <Dropdown
                        value={dsIdentityDocumentType}
                        options={(identityDocumentTypeOptions || []).map(p => ({ label: p.value, value: p.key }))}
                        onChange={(e) => setDsIdentityDocumentType(e.value)}
                        showClear
                        placeholder={t('dsSelect')}
                      />
                    </div>
                  </div>
                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldIdentityDocumentNumber')}</label>
                      <InputText
                        value={dsIdentityDocumentNumber}
                        onChange={(e) => setDsIdentityDocumentNumber(e.target.value)}
                      />
                    </div>
                  </div>


                  {/* <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldIdentityDocumentValidUntil')}</label>
                      <div className={"p-grid"}>
                        <div className={"p-col-12"}>
                          <InputMask
                            value={dsIdentityDocumentValidUntil || ''}
                            mask={"9999-99-99"}
                            placeholder={t('dsDateFormat')}
                            className={dsIdentityDocumentValidUntil ? 'p-invalid' : ''}
                            onChange={(e) => setDsIdentityDocumentValidUntil(e.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div> */}

                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('columnPhoneNumber')}</label>
                      <InputText
                        value={dsPhoneNumber}
                        onChange={(e) => setDsPhoneNumber(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldBirthPlace')}</label>
                      <InputText
                        value={dsBirthPlace}
                        onChange={(e) => setDsBirthPlace(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldBirthDay')}</label>
                      <div className={"p-grid"}>
                        <div className={"p-col-12"}>
                          <InputMask
                            value={dsBirthDay || ''}
                            mask={"9999-99-99"}
                            placeholder={t('dsDateFormat')}
                            className={dsBirthDay ? 'p-invalid' : ''}
                            onChange={(e) => setDsBirthDay(e.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('fieldCitizenship')}</label>
                      <Dropdown
                        value={dsCitizenship}
                        options={(citizenshipOptions || []).map(p => ({ label: p.name, value: p.id }))}
                        onChange={(e) => setDsCitizenship(e.value)}
                        showClear
                        placeholder={t('dsSelect')}
                      />
                    </div>
                  </div>

                  <div className={"p-col-12 p-lg-2"}>
                    <div className="p-field p-fluid">
                      <label>{t('columnActive')}</label>
                      <span className="p-d-block">
                        <TriStateCheckbox
                          value={dsActive}
                          onChange={(e) => setDsActive(e.value)}
                        />
                      </span>
                    </div>
                  </div>

                  <div className={"p-col-12 p-text-right"}>
                    <Button
                      type="button"
                      label={t('dsClearCriteria')}
                      icon="pi pi-times"
                      className="p-button-danger p-mr-2"
                      onClick={() => {
                        setDsLastName('');
                        setDsFirstName('');
                        setDsEmail('');
                        setDsInsuranceNumber('');
                        setDsTaxNumber('');
                        setDsIdentityDocumentValidUntil('');
                        setDsPhoneNumber('');
                        setDsBirthDay('');
                        setDsEmployeePosition(null);
                        setDsIdentityDocumentType(null);
                        setDsIdentityDocumentNumber('');
                        setDsCitizenship(null);
                        setDsActive(null);
                        setDsNote('');
                        setDetailedSearchQuery('');
                        clearDetailedFormPersist();
                        localStorage.removeItem(LIST_STORAGE_KEY);
                        setLazyParams({
                          first: 0,
                          rows: rows,
                          page: 0,
                          sortField: 'id',
                          sortOrder: 1,
                        });
                      }}
                    />
                    <Button
                      type="button"
                      label={t('dsSearch')}
                      icon="pi pi-search"
                      className="p-button-primary"
                      onClick={() => {
                        const q = buildDetailedSearchQuery();
                        setDetailedSearchQuery(q);
                        persistDetailedForm();
                        setLazyParams({
                          ...lazyParams,
                          first: 0,
                          page: 0,
                        });
                      }}
                    />
                  </div>
                </div>
              </Panel>
            }
      <DataTable size={localStorage.getItem('scale')}
                 emptyMessage={t('noResults')}
                 value={employees} paginator first={lazyParams.first} totalRecords={totalRecords} onPage={onPage}
                 onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                 onFilter={onFilter} filters={lazyParams.filters} loading={loading} lazy
                 paginatorTemplate={pager} rows={rows}
                 autoLayout={true}
                 filterDisplay="row"
                 responsiveLayout="scroll"
                 stripedRows
                 ref={dt}
                 dataKey="id"
                 selectionMode={"single"}  
                 onRowDoubleClick={(rowData) => {
                  if (window.App.urlVars().search === 'employee') {
                    const currentVars = window.App.urlVars();
                    const cleanedVars = { ...currentVars };
                    delete cleanedVars.search;
                    delete cleanedVars.employeeId;
                    delete cleanedVars.employeeName;
                    const fullName =
                      (rowData.data.first_name ? rowData.data.first_name : '') +
                      ' ' +
                      (rowData.data.last_name ? rowData.data.last_name : '');
                    cleanedVars.employeeId = rowData.data.id;
                    cleanedVars.employeeName = encodeURIComponent(fullName.trim());
                    let query = '?';
                    Object.entries(cleanedVars).forEach(([key, value]) => {
                      if (value !== undefined && value !== null) {
                        query += key + '=' + value + '&';
                      }
                    });
                    history.push(props.backlink + query);
                  }
                }}
      >
        <Column field="active" header={t('columnActive')} bodyClassName="col-body-font-bold" sortable body={(rowData) => {
          return (
            <>
              {rowData.active === true ? (
                <i className={"pi pi-check text-success"}></i>
              ) : (
                <i className={"pi pi-times text-danger"}></i>
              )}
            </>
          )}}></Column>
        <Column field="last_name" header={t('columnLastName')} bodyClassName="col-body-font-bold" sortable></Column>
        <Column field="first_name" header={t('columnFirstName')} bodyClassName="col-body-font-bold" sortable></Column>
        <Column field="employee_position.name" header={t('columnPosition')}></Column>
        <Column field="email" header={t('columnEmail')} sortable></Column>
        <Column field="birth_day" header={t('columnDateOfBirth')} sortable body={(rowData) => (
            <>
              {rowData.birth_day ? window.App.formatDateValue(rowData.birth_day) : ''}
            </>
          )}
        />
        <Column field="created_at" header={t('columnCreatedAt')} sortable body={(rowData) => {
          return (
            <>{window.App.formatDateValue(rowData.created_at)}</>
          )
        }}></Column>
        <Column field="updated_at" header={t('columnUpdatedAt')} sortable body={(rowData) => {
          return (
            <>{window.App.formatDateValue(rowData.updated_at)}</>
          )
        }}></Column>
        {/* <Column body={actionBodyTemplate} className={"p-py-0 p-text-right p-text-nowrap"}></Column> */}
        {props.view !== 'search' && (
          <Column
            body={actionBodyTemplate}
            className={"p-py-0 p-text-right p-text-nowrap"}
          />
        )}
      </DataTable>
    </>
  )
}
export default withTranslation('common')(EmployeeListComponent);
